import type { ClientOverview, ClientUnit, Unit } from '../../types/api';

export interface PortfolioSummaryStats {
  propertyCount: number;
  cityCount: number;
  currencyCount: number;
  operatingModelCount: number;
}

const emptyStats: PortfolioSummaryStats = {
  propertyCount: 0,
  cityCount: 0,
  currencyCount: 0,
  operatingModelCount: 0,
};

function normalizeKey(value?: string | null): string | null {
  const trimmed = value?.trim();
  return trimmed ? trimmed.toLowerCase() : null;
}

function pluralize(count: number, singular: string, plural: string): string {
  return count === 1 ? singular : plural;
}

export function calculatePortfolioSummaryStats(
  clientUnits: ClientUnit[],
  allUnits: Unit[],
  overviewData: ClientOverview[] = []
): PortfolioSummaryStats {
  if (!clientUnits.length) {
    return emptyStats;
  }

  const clientUnitIds = new Set(
    clientUnits
      .map((unit) => unit.unit_id_no)
      .filter((unitId): unitId is number => unitId != null)
  );

  if (clientUnitIds.size === 0) {
    return emptyStats;
  }

  const portfolioUnits = allUnits.filter((unit) => clientUnitIds.has(unit.unit_id_no));

  const cities = new Set<string>();
  const currencies = new Set<string>();
  const operatingModels = new Set<string>();

  for (const unit of portfolioUnits) {
    const cityKey = normalizeKey(unit.city);
    if (cityKey) {
      cities.add(cityKey);
    }

    const currencyKey = normalizeKey(unit.currency);
    if (currencyKey) {
      currencies.add(currencyKey);
    }

    const operatingModelKey = normalizeKey(unit.operating_model);
    if (operatingModelKey) {
      operatingModels.add(operatingModelKey);
    }
  }

  for (const unit of clientUnits) {
    const operatingModelKey = normalizeKey(unit.operating_model);
    if (operatingModelKey) {
      operatingModels.add(operatingModelKey);
    }
  }

  for (const overview of overviewData) {
    const currencyKey = normalizeKey(overview.unit_currency);
    if (currencyKey) {
      currencies.add(currencyKey);
    }
  }

  return {
    propertyCount: clientUnitIds.size,
    cityCount: cities.size,
    currencyCount: currencies.size,
    operatingModelCount: operatingModels.size,
  };
}

export function formatPortfolioSummaryTags(stats: PortfolioSummaryStats): string[] {
  return [
    `${stats.propertyCount} ${pluralize(stats.propertyCount, 'Property', 'Properties')}`,
    `${stats.cityCount} ${pluralize(stats.cityCount, 'City', 'Cities')}`,
    `${stats.currencyCount} ${pluralize(stats.currencyCount, 'Currency', 'Currencies')}`,
    `${stats.operatingModelCount} ${pluralize(stats.operatingModelCount, 'Operating Model', 'Operating Models')}`,
  ];
}
